import { useEffect } from 'react';
import { getSupabase } from '../../lib/supabase';
import { useSEOHead, SITE_URL, DEFAULT_OG_IMAGE } from '../../hooks/useSEOHead';
import { PHONE_DISPLAY, BUSINESS_NAME, EMAIL, ADDRESS_STREET, ADDRESS_CITY, ADDRESS_STATE, ADDRESS_ZIP, GEO_LATITUDE, GEO_LONGITUDE } from '../../lib/businessInfo';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

export default function InventorySEO() {
  useSEOHead({
    title: `Used Cars for Sale Warrenton OR | Quality Pre-Owned Vehicles | ${BUSINESS_NAME}`,
    description: `Browse quality used cars, trucks, and SUVs in Warrenton, Oregon. Every vehicle inspected, detailed, and ready to drive. Serving Astoria, Seaside, Cannon Beach, Tillamook, Portland, and SW Washington. No hidden fees. Call ${PHONE_DISPLAY}.`,
    keywords: 'used cars Warrenton Oregon, pre-owned vehicles Astoria, cars Seaside Oregon, SUVs Tillamook, trucks Cannon Beach, auto dealer Clatsop County, used cars Portland Oregon, vehicles Longview Washington, cars Kelso WA, Pacific Northwest auto broker, no dealer fees Oregon, transparent pricing used cars',
    canonicalUrl: `${SITE_URL}/inventory`,
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: `${BUSINESS_NAME} — Used Cars Warrenton OR`,
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: `${GEO_LATITUDE};${GEO_LONGITUDE}`,
  });

  useEffect(() => {
    const BASE = SITE_URL;

    // ── WebPage Schema with Breadcrumb ────────────────────────────────────────
    injectJsonLd('schema-inventory-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE}/inventory#webpage`,
      name: `Used Cars Warrenton OR | Inventory | ${BUSINESS_NAME}`,
      description: `Browse quality used cars, trucks, and SUVs in Warrenton, Astoria, Seaside, Tillamook, and across the Pacific Northwest. Transparent pricing, no hidden fees, all makes and models.`,
      url: `${BASE}/inventory`,
      isPartOf: { '@type': 'WebSite', '@id': `${BASE}/#website`, name: BUSINESS_NAME, url: BASE },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${BASE}/inventory#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
          { '@type': 'ListItem', position: 2, name: 'Inventory', item: `${BASE}/inventory` },
        ],
      },
      about: {
        '@type': 'AutomotiveBusiness',
        '@id': `${BASE}/#business`,
        name: BUSINESS_NAME,
      },
      mainEntity: {
        '@type': 'ItemList',
        '@id': `${BASE}/inventory#itemlist`,
        name: `${BUSINESS_NAME} Vehicle Inventory`,
      },
    });

    // ── LocalBusiness + AutomotiveBusiness Schema ─────────────────────────────
    injectJsonLd('schema-inventory-local-business', {
      '@context': 'https://schema.org',
      '@type': ['LocalBusiness', 'AutomotiveBusiness', 'AutoDealer'],
      '@id': `${BASE}/#business`,
      name: BUSINESS_NAME,
      description: `Used car dealer serving Warrenton OR, Astoria, Seaside, Cannon Beach, Tillamook, Portland, and SW Washington. Quality pre-owned vehicles, transparent pricing, no dealer games, $250 flat doc fee.`,
      url: BASE,
      telephone: '+18554686348',
      email: EMAIL,
      address: {
        '@type': 'PostalAddress',
        streetAddress: ADDRESS_STREET,
        addressLocality: ADDRESS_CITY,
        addressRegion: ADDRESS_STATE,
        postalCode: ADDRESS_ZIP,
        addressCountry: 'US',
      },
      geo: { '@type': 'GeoCoordinates', latitude: GEO_LATITUDE, longitude: GEO_LONGITUDE },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          description: 'By Appointment Only',
        },
      ],
      areaServed: [
        { '@type': 'City', name: 'Warrenton', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Astoria', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Seaside', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Cannon Beach', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Gearhart', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Tillamook', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Portland', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Longview', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'City', name: 'Kelso', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'City', name: 'Long Beach', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'City', name: 'Ilwaco', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'County', name: 'Clatsop County', containedInPlace: { '@type': 'State', name: 'Oregon' } },
      ],
      hasMap: `https://maps.google.com/?q=${ADDRESS_STREET},+${ADDRESS_CITY},+${ADDRESS_STATE}+${ADDRESS_ZIP}`,
      priceRange: '$$',
      currenciesAccepted: 'USD',
      paymentAccepted: 'Cash, Credit Card, Financing',
    });

    // ── WebSite SearchAction (Sitelinks Searchbox) ────────────────────────────
    injectJsonLd('schema-inventory-sitelinks-searchbox', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${BASE}/#website`,
      name: BUSINESS_NAME,
      url: BASE,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${BASE}/inventory?search={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    });

    // ── FAQPage Schema ────────────────────────────────────────────────────────
    injectJsonLd('schema-inventory-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${BASE}/inventory#faqpage`,
      name: 'Used Cars Warrenton OR — Frequently Asked Questions',
      url: `${BASE}/inventory`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Where can I find used cars for sale in Warrenton, Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${BUSINESS_NAME} is located at ${ADDRESS_STREET}, ${ADDRESS_CITY}, ${ADDRESS_STATE} ${ADDRESS_ZIP} — right on the Oregon Coast. We carry quality pre-owned cars, trucks, and SUVs with transparent pricing and no hidden fees. Browse our full inventory online or contact us at ${PHONE_DISPLAY} to schedule a visit.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Does Rockhound Auto charge dealer fees in Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `No. ${BUSINESS_NAME} charges a flat $250 documentation fee — that's it. No prep fees, no processing fees, no surprise add-ons at signing. The price you see is the price you pay. We believe transparent pricing builds trust.`,
          },
        },
        {
          '@type': 'Question',
          name: 'What is a no-fee car dealer in Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `A no-fee car dealer in Oregon means the dealership does not charge excessive documentation, processing, or dealer prep fees on top of the vehicle price. At ${BUSINESS_NAME} in Warrenton, OR, we charge a flat $250 doc fee — no hidden charges added at the end of the deal.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Do you deliver used cars to Astoria, Seaside, or Tillamook?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes — ${BUSINESS_NAME} offers free vehicle delivery within 50 miles across the Oregon Coast, including Astoria, Seaside, Cannon Beach, Gearhart, Tillamook, and surrounding areas. We also serve SW Washington including Long Beach, Ilwaco, Longview, and Kelso. Contact us at ${PHONE_DISPLAY} to arrange delivery to your location.`,
          },
        },
        {
          '@type': 'Question',
          name: 'How do I buy a used car in Warrenton, Oregon without getting ripped off?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `At ${BUSINESS_NAME} in Warrenton, OR, every vehicle is priced transparently with a flat $250 doc fee. We provide full vehicle history, inspection reports, and honest answers about each car. We also offer a broker service to help you find and negotiate any vehicle — not just our own inventory.`,
          },
        },
        {
          '@type': 'Question',
          name: 'What used cars are available under $15,000 near Warrenton, Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `${BUSINESS_NAME} regularly stocks quality used cars under $15,000 in Warrenton, Oregon. Inventory changes frequently — browse our live inventory page at ${BASE}/inventory or sign up for Deal Radar alerts to get notified the moment a vehicle in your budget becomes available.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get financing for a used car in Warrenton, OR with bad credit?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes. ${BUSINESS_NAME} works with multiple lenders to help buyers with all credit situations in Warrenton, Oregon and across the Oregon Coast. You can apply for financing online at ${BASE}/apply-for-financing and get pre-approved before visiting. We focus on finding the best rate for your situation, not just the easiest approval.`,
          },
        },
      ],
    });

    // ── Dynamic ItemList + AggregateOffer from Supabase ───────────────────────
    getSupabase().then((supabase) => {
      supabase
        .from('vehicles')
        .select('price, make, model, year, id')
        .eq('status', 'available')
        .order('price', { ascending: true })
        .limit(50)
        .then(({ data, error }) => {
          if (error || !data || data.length === 0) return;

          const prices = data.map((v) => Number(v.price)).filter((p) => p > 0);
          if (prices.length === 0) return;

          const lowPrice = Math.min(...prices);
          const highPrice = Math.max(...prices);
          const offerCount = prices.length;

          // ── ItemList Schema ───────────────────────────────────────────────────
          injectJsonLd('schema-inventory-itemlist', {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            '@id': `${BASE}/inventory#itemlist`,
            name: `${BUSINESS_NAME} Vehicle Inventory — Used Cars Warrenton Oregon`,
            description: `Browse ${offerCount} quality pre-owned vehicles at ${BUSINESS_NAME} in Warrenton, Oregon. Serving Astoria, Seaside, Cannon Beach, Tillamook, Portland, and the Pacific Northwest. Transparent pricing, no hidden fees.`,
            url: `${BASE}/inventory`,
            numberOfItems: offerCount,
            itemListElement: data.slice(0, 10).map((v, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${BASE}/inventory/${v.id}`,
              name: `${v.year} ${v.make} ${v.model}`,
            })),
          });

          // ── AggregateOffer Schema ─────────────────────────────────────────────
          injectJsonLd('schema-inventory-aggregate-offer', {
            '@context': 'https://schema.org',
            '@type': 'AggregateOffer',
            '@id': `${BASE}/inventory#aggregateoffer`,
            name: `Used Cars for Sale — ${BUSINESS_NAME}`,
            priceCurrency: 'USD',
            lowPrice,
            highPrice,
            offerCount,
            validFrom: new Date().toISOString().split('T')[0],
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/UsedCondition',
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'US',
              returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
              merchantReturnDays: 3,
              returnMethod: 'https://schema.org/ReturnInStore',
              returnFees: 'https://schema.org/FreeReturn',
              refundType: 'https://schema.org/FullRefund',
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'USD' },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'US',
                addressRegion: ['OR', 'WA', 'CA', 'ID', 'NV', 'MT', 'CO'],
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
                transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
              },
            },
            offers: data.slice(0, 5).map((v) => ({
              '@type': 'Offer',
              name: `${v.year} ${v.make} ${v.model}`,
              price: Number(v.price),
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              itemCondition: 'https://schema.org/UsedCondition',
              url: `${BASE}/inventory/${v.id}`,
              validFrom: new Date().toISOString().split('T')[0],
              seller: { '@type': 'AutomotiveBusiness', name: BUSINESS_NAME },
            })),
            seller: {
              '@type': 'AutomotiveBusiness',
              '@id': `${BASE}/#business`,
              name: BUSINESS_NAME,
              url: BASE,
              telephone: '+18554686348',
              address: {
                '@type': 'PostalAddress',
                streetAddress: ADDRESS_STREET,
                addressLocality: ADDRESS_CITY,
                addressRegion: ADDRESS_STATE,
                postalCode: ADDRESS_ZIP,
                addressCountry: 'US',
              },
            },
          });
        });
    });

    // ── Dynamic AggregateRating from Approved Reviews ─────────────────────────
    getSupabase().then((supabase) => {
      supabase
        .from('reviews')
        .select('rating')
        .eq('status', 'approved')
        .then(({ data, error }) => {
          if (error || !data || data.length === 0) return;
          const ratings = data.map((r) => Number(r.rating)).filter((r) => r > 0);
          if (ratings.length === 0) return;
          const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
          injectJsonLd('schema-inventory-aggregate-rating', {
            '@context': 'https://schema.org',
            '@type': ['AutoDealer', 'LocalBusiness'],
            '@id': `${BASE}/#auto-dealer`,
            name: BUSINESS_NAME,
            url: `${BASE}/inventory`,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: Math.round(avg * 10) / 10,
              reviewCount: ratings.length,
              bestRating: 5,
              worstRating: 1,
            },
          });
        });
    });

    return () => {
      removeJsonLd('schema-inventory-webpage');
      removeJsonLd('schema-inventory-local-business');
      removeJsonLd('schema-inventory-sitelinks-searchbox');
      removeJsonLd('schema-inventory-faq');
      removeJsonLd('schema-inventory-itemlist');
      removeJsonLd('schema-inventory-aggregate-offer');
      removeJsonLd('schema-inventory-aggregate-rating');
    };
  }, []);

  return null;
}